import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance
const api = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add token to requests if available
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Handle response errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth APIs
export const authAPI = {
    register: (userData) => api.post('/auth/register', userData),
    login: (credentials) => api.post('/auth/login', credentials),
    setPassword: (data) => api.post('/auth/set-password', data),
    requestReset: (data) => api.post('/auth/request-reset', data),
    confirmReset: (data) => api.post('/auth/confirm-reset', data),
};

// Two-Factor Auth APIs
export const twoFAAPI = {
    status: () => api.get('/auth/2fa/status'),
    setup: () => api.post('/auth/2fa/setup'),
    verifySetup: (data) => api.post('/auth/2fa/verify-setup', data),
    verify: (data) => api.post('/auth/2fa/verify', data),
    disable: () => api.post('/auth/2fa/disable'),
};

// Subscriptions APIs
export const subscriptionsAPI = {
    getAll: () => api.get('/subscriptions'),
    getById: (id) => api.get(`/subscriptions/${id}`),
    create: (data) => api.post('/subscriptions', data),
    update: (id, data) => api.put(`/subscriptions/${id}`, data),
    delete: (id) => api.delete(`/subscriptions/${id}`)
};

// Bills APIs
export const billsAPI = {
    getAll: () => api.get('/bills'),
    getById: (id) => api.get(`/bills/${id}`),
    create: (data) => api.post('/bills', data),
    update: (id, data) => api.put(`/bills/${id}`, data),
    togglePaid: (id, isPaid) => api.patch(`/bills/${id}/paid`, { is_paid: isPaid }),
    getPayments: (id) => api.get(`/bills/${id}/payments`),
    delete: (id) => api.delete(`/bills/${id}`)
};

// Grocery APIs
export const groceryAPI = {
    getAll: () => api.get('/grocery'),
    getById: (id) => api.get(`/grocery/${id}`),
    create: (data) => api.post('/grocery', data),
    update: (id, data) => api.put(`/grocery/${id}`, data),
    toggle: (id) => api.patch(`/grocery/${id}/toggle`),
    delete: (id) => api.delete(`/grocery/${id}`),
    clearChecked: () => api.delete('/grocery/checked/clear')
};

// Todo APIs
export const todoAPI = {
    getAll: () => api.get('/todo'),
    getById: (id) => api.get(`/todo/${id}`),
    create: (data) => api.post('/todo', data),
    update: (id, data) => api.put(`/todo/${id}`, data),
    toggle: (id) => api.patch(`/todo/${id}/toggle`),
    delete: (id) => api.delete(`/todo/${id}`),
    clearCompleted: () => api.delete('/todo/completed/clear')
};

// Knowledge Base — Categories
export const kbAPI = {
    listCategories: () => api.get('/kb/categories'),
    createCategory: (data) => api.post('/kb/categories', data),
    updateCategory: (id, data) => api.put(`/kb/categories/${id}`, data),
    deleteCategory: (id) => api.delete(`/kb/categories/${id}`),
    reorderCategories: (order) => api.put('/kb/categories/reorder', { order }),

    listArticles: (params = {}) => api.get('/kb/articles', { params }),
    getArticle: (id) => api.get(`/kb/articles/${id}`),
    createArticle: (data) => api.post('/kb/articles', data),
    updateArticle: (id, data) => api.put(`/kb/articles/${id}`, data),
    deleteArticle: (id) => api.delete(`/kb/articles/${id}`),

    listRecipeCategories: () => api.get('/kb/recipe-categories'),
    createRecipeCategory: (data) => api.post('/kb/recipe-categories', data),
    updateRecipeCategory: (id, data) => api.put(`/kb/recipe-categories/${id}`, data),
    deleteRecipeCategory: (id) => api.delete(`/kb/recipe-categories/${id}`),
    reorderRecipeCategories: (order) => api.put('/kb/recipe-categories/reorder', { order }),

    listRecipes: (params = {}) => api.get('/kb/recipes', { params }),
    getRecipe: (id) => api.get(`/kb/recipes/${id}`),
    createRecipe: (data) => api.post('/kb/recipes', data),
    updateRecipe: (id, data) => api.put(`/kb/recipes/${id}`, data),
    deleteRecipe: (id) => api.delete(`/kb/recipes/${id}`),
};

// Debts APIs
export const debtsAPI = {
    getAll: () => api.get('/debts'),
    getById: (id) => api.get(`/debts/${id}`),
    getTotals: () => api.get('/debts/totals'),
    create: (data) => api.post('/debts', data),
    update: (id, data) => api.put(`/debts/${id}`, data),
    delete: (id) => api.delete(`/debts/${id}`)
};

// Web Links APIs
export const webLinksAPI = {
    getAll: (params) => api.get('/weblinks', { params }),
    create: (data) => api.post('/weblinks', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    update: (id, data) => api.put(`/weblinks/${id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    delete: (id) => api.delete(`/weblinks/${id}`)
};

// Passwords APIs
export const passwordsAPI = {
    getAll: (params) => api.get('/passwords', { params }),
    reveal: (id) => api.get(`/passwords/${id}/reveal`),
    create: (data) => api.post('/passwords', data),
    update: (id, data) => api.put(`/passwords/${id}`, data),
    delete: (id) => api.delete(`/passwords/${id}`),
    logAction: (id, action) => api.post(`/passwords/${id}/log`, { action }),
    getSecurityQuestions: (id) => api.get(`/passwords/${id}/security-questions`),
    saveSecurityQuestions: (id, questions) => api.put(`/passwords/${id}/security-questions`, { questions }),
    getAuditLog: () => api.get('/passwords/audit'),
    pinStatus: () => api.get('/passwords/pin/status'),
    pinSet: (pin) => api.post('/passwords/pin/set', { pin }),
    pinVerify: (pin) => api.post('/passwords/pin/verify', { pin })
};

// Appointments APIs
export const appointmentsAPI = {
    getAll: () => api.get('/appointments'),
    getById: (id) => api.get(`/appointments/${id}`),
    create: (data) => api.post('/appointments', data),
    update: (id, data) => api.put(`/appointments/${id}`, data),
    delete: (id) => api.delete(`/appointments/${id}`),
};

// Assets APIs
export const assetsAPI = {
    getAll: () => api.get('/assets'),
    create: (data) => api.post('/assets', data),
    update: (id, data) => api.put(`/assets/${id}`, data),
    delete: (id) => api.delete(`/assets/${id}`),
};

// Wish List APIs
export const wishlistAPI = {
    getAll: () => api.get('/wishlist'),
    create: (data) => api.post('/wishlist', data),
    update: (id, data) => api.put(`/wishlist/${id}`, data),
    toggle: (id) => api.patch(`/wishlist/${id}/toggle`),
    delete: (id) => api.delete(`/wishlist/${id}`),
};

export default api;
